import {
  areConnectorDocumentIdentitiesEqual,
  connectorTargetBlockMessage,
  decideConnectorTargetTransition,
  toAppliedTarget,
} from './connectorTargetCheckpoint';
import type {
  ConnectorDocumentIdentity,
  ConnectorTargetBlockReason,
  ConnectorTargetRequest,
  ConnectorTargetSelection,
} from './connectorTargetCheckpoint';
import type {
  ConnectorAppliedTarget,
  ConnectorParameterUndoPlan,
} from '../../types/parameterSetupUndo';

export type { ConnectorTargetRequest } from './connectorTargetCheckpoint';

/**
 * Plain apply result, so this module never depends on the parameter-addition
 * hooks it is driven by. The adapter maps the rich hook result down to this.
 */
export interface ConnectorApplyOutcome {
  success: boolean;
  error?: string;
}

export interface ConnectorTargetPorts {
  readCheckpoint: () => ConnectorParameterUndoPlan | null;
  readActiveIdentity: () => ConnectorDocumentIdentity | null;
  undoAppliedTarget: () => Promise<boolean>;
  applyTarget: (target: ConnectorTargetSelection) => Promise<ConnectorApplyOutcome>;
  stampAppliedTarget: (applied: ConnectorAppliedTarget) => void;
  invalidateModelCache: () => void;
}

export type ConnectorTargetPhase = 'undo' | 'identity' | 'apply';

export type ConnectorTargetOutcome =
  | { kind: 'applied' }
  | { kind: 'unchanged' }
  | { kind: 'blocked'; reason: ConnectorTargetBlockReason; message: string }
  /** Nothing was changed in the model; the user can simply retry. */
  | { kind: 'failed'; phase: ConnectorTargetPhase; message: string }
  /** The model was left mid-replacement; forward steps must stay locked. */
  | { kind: 'recovery-required'; phase: ConnectorTargetPhase; message: string };

const UndoFailedMessage =
  'The previously applied connector parameters could not be undone, so the new target was not applied. Retry, or remove them manually before continuing.';

const IdentityChangedDuringReplacementMessage =
  'The active Revit document changed while the connector target was being replaced. The previous connector parameters were already undone, so recheck the project before applying the target again.';

const IdentityChangedBeforeVerificationMessage =
  'The active Revit document changed before the new connector target could be verified, so it was not recorded as applied. Recheck the project and apply the target again.';

const joinMessage = (lead: string, detail?: string): string =>
  detail ? `${lead} ${detail}` : lead;

const applyFailedMessage = (error?: string): string =>
  joinMessage('The new connector target could not be applied.', error);

const applyFailedAfterUndoMessage = (error?: string): string =>
  joinMessage(
    'The previously applied connector parameters were undone, but the new target could not be applied, so this project currently has no connector parameters. Apply the target again to recover.',
    error
  );

const blocked = (reason: ConnectorTargetBlockReason): ConnectorTargetOutcome => ({
  kind: 'blocked',
  reason,
  message: connectorTargetBlockMessage(reason),
});

const isActiveDocumentUnchanged = (
  ports: ConnectorTargetPorts,
  request: ConnectorTargetRequest
): boolean => {
  const identity = ports.readActiveIdentity();
  return identity !== null && areConnectorDocumentIdentitiesEqual(identity, request);
};

/** Reverses the applied target. Returns an outcome only when it must stop. */
const reverseAppliedTarget = async (
  ports: ConnectorTargetPorts,
  request: ConnectorTargetRequest
): Promise<ConnectorTargetOutcome | null> => {
  const reverted = await ports.undoAppliedTarget();
  if (!reverted) {
    return { kind: 'failed', phase: 'undo', message: UndoFailedMessage };
  }

  if (!isActiveDocumentUnchanged(ports, request)) {
    return {
      kind: 'recovery-required',
      phase: 'identity',
      message: IdentityChangedDuringReplacementMessage,
    };
  }

  return null;
};

const applyRequestedTarget = async (
  ports: ConnectorTargetPorts,
  request: ConnectorTargetRequest,
  undone: boolean
): Promise<ConnectorTargetOutcome> => {
  const result = await ports.applyTarget(request.target);
  if (!result.success) {
    return undone
      ? {
          kind: 'recovery-required',
          phase: 'apply',
          message: applyFailedAfterUndoMessage(result.error),
        }
      : { kind: 'failed', phase: 'apply', message: applyFailedMessage(result.error) };
  }

  if (!isActiveDocumentUnchanged(ports, request)) {
    return {
      kind: 'recovery-required',
      phase: 'identity',
      message: IdentityChangedBeforeVerificationMessage,
    };
  }

  ports.stampAppliedTarget(toAppliedTarget(request));
  ports.invalidateModelCache();
  return { kind: 'applied' };
};

/**
 * Replaces the connector target the model currently holds with the requested
 * one: reverse first, apply once, record provenance only after a verified
 * same-document success.
 */
export const runConnectorTargetTransaction = async (
  request: ConnectorTargetRequest,
  ports: ConnectorTargetPorts
): Promise<ConnectorTargetOutcome> => {
  // Read once: the stored checkpoint can be cleared underneath an await.
  const checkpoint = ports.readCheckpoint();
  const decision = decideConnectorTargetTransition(checkpoint, request);
  if (decision.kind === 'blocked') {
    return blocked(decision.reason);
  }

  if (!isActiveDocumentUnchanged(ports, request)) {
    return blocked('document-changed');
  }

  if (decision.kind === 'reapply-unchanged') {
    return { kind: 'unchanged' };
  }

  const undone = decision.kind === 'undo-then-apply';
  if (undone) {
    const reversalFailure = await reverseAppliedTarget(ports, request);
    if (reversalFailure) {
      return reversalFailure;
    }
  }

  return applyRequestedTarget(ports, request, undone);
};
